import { useState, type FormEvent } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/auth/useAuth'
import { AuthLoading } from '@/auth/guards'
import { isSupabaseConfigured } from '@/lib/supabase'
import { Alert, Button, FormField, Input } from '@/components/ui'
import './auth-page.css'

/**
 * Anmeldung. E-Mail und Passwort, Registrierung ist abgeschaltet — Konten
 * entstehen ausschliesslich per Einladung.
 */
export function Login() {
  const { session, signIn, requestPasswordReset } = useAuth()
  const [params] = useSearchParams()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  if (session === undefined) return <AuthLoading />

  // Bereits angemeldet: dorthin, wo die Person eigentlich hinwollte.
  if (session) {
    const target = params.get('returnTo')
    return <Navigate to={target && target.startsWith('/') ? target : '/'} replace />
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    setNotice(null)

    const { error: signInError } = await signIn(email, password)
    if (signInError) {
      // Bewusst ohne Unterscheidung zwischen "Konto unbekannt" und "Passwort
      // falsch": das waere sonst ein Verzeichnis gueltiger Adressen.
      setError(
        signInError.toLowerCase().includes('invalid')
          ? 'E-Mail-Adresse oder Passwort stimmt nicht.'
          : signInError,
      )
    }
    setBusy(false)
  }

  async function handleReset() {
    if (!email.trim()) {
      setError('Für das Zurücksetzen bitte zuerst die E-Mail-Adresse eintragen.')
      return
    }
    setBusy(true)
    setError(null)
    const { error: resetError } = await requestPasswordReset(email)
    setBusy(false)

    if (resetError) {
      setError(resetError)
    } else {
      setNotice(
        'Falls zu dieser Adresse ein Konto besteht, ist eine E-Mail zum Zurücksetzen unterwegs.',
      )
    }
  }

  return (
    <div className="auth-page grid-backdrop">
      <div className="auth-page__panel">
        <div className="auth-page__brand">
          <svg viewBox="0 0 28 28" className="auth-page__mark" aria-hidden="true">
            <path
              d="M5 22V6l18 16V6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.6"
              strokeLinecap="square"
            />
          </svg>
          <p className="eyebrow">NEURA Performance</p>
        </div>

        <h1 className="auth-page__title">Anmelden</h1>
        <p className="auth-page__lead">
          Internes Performance-Intranet. Zugang ausschließlich über Einladung.
        </p>

        {!isSupabaseConfigured && (
          <Alert tone="warning" title="Nicht konfiguriert">
            <code>VITE_SUPABASE_URL</code> und <code>VITE_SUPABASE_ANON_KEY</code> fehlen.
            Ohne sie kann keine Anmeldung stattfinden.
          </Alert>
        )}

        <form className="auth-page__form" onSubmit={handleSubmit}>
          <FormField label="E-Mail" required>
            {(field) => (
              <Input
                {...field}
                type="email"
                autoComplete="username"
                autoFocus
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            )}
          </FormField>

          <FormField label="Passwort" required>
            {(field) => (
              <Input
                {...field}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            )}
          </FormField>

          {error && <Alert tone="danger">{error}</Alert>}
          {notice && <Alert tone="success">{notice}</Alert>}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            block
            loading={busy}
            disabled={!isSupabaseConfigured}
          >
            Anmelden
          </Button>

          <button type="button" className="auth-page__link" onClick={handleReset} disabled={busy}>
            Passwort vergessen?
          </button>
        </form>
      </div>

      <p className="auth-page__footnote">
        Zugriffsrechte werden serverseitig durchgesetzt. Jede Anmeldung und jede
        Rechteänderung wird protokolliert.
      </p>
    </div>
  )
}
